import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ErrorResponse } from '../../api';
import { PaginatedResponse } from '../../api/PaginatedReponse';
import {
  fetchPoll,
  fetchPolls,
  fetchFilteredPolls,
  submitPoll,
  getNumberOfActiveAndClosedPolls,
} from './actions';
import { Poll } from './models/Poll';
import { PollWithQuestionsList } from './models/PollWithQuestionsList';

export interface PollsState {
  polls: PaginatedResponse<Poll[]>;
  filteredPolls: PaginatedResponse<Poll[]>;
  pollWithQuestionsList?: PollWithQuestionsList;
  pollWithQuestionsListLoading: boolean;
  totalPollCount: number;
  loadingPolls: boolean;
  totalFilteredPollCount: number;
  loadingFilteredPolls: boolean;
  loadingSubmitPoll: boolean;
  pollSubmissionError?: ErrorResponse;
  isCreateNewPollDialogOpened: boolean;
  editedPoll?: Poll;
  numberOfActivePolls: number;
  numberOfClosedPolls: number;
  pollsError?: ErrorResponse;
}

const initialState: PollsState = {
  polls: { data: [] },
  filteredPolls: { data: [] },
  pollWithQuestionsList: undefined,
  pollWithQuestionsListLoading: false,
  totalPollCount: 0,
  numberOfActivePolls: 0,
  numberOfClosedPolls: 0,
  loadingPolls: false,
  totalFilteredPollCount: 0,
  loadingFilteredPolls: false,
  loadingSubmitPoll: false,
  pollSubmissionError: undefined,
  isCreateNewPollDialogOpened: false,
  editedPoll: undefined,
  pollsError: undefined,
};

const pollsSlice = createSlice({
  name: 'polls',
  initialState,
  reducers: {
    clearFilteredPolls: (state) => {
      state.filteredPolls.data = [];
    },
    clearPolls: (state) => {
      state.polls.data = [];
    },
    clearPollWithQuestions: (state) => {
      state.pollWithQuestionsList = undefined;
    },
    setCreateNewPollDialogOpened: (state, action: PayloadAction<boolean>) => {
      state.isCreateNewPollDialogOpened = action.payload;
    },
    setEditedPoll: (state, action: PayloadAction<Poll | undefined>) => {
      state.editedPoll = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPolls.pending, (state) => {
        state.loadingPolls = true;
      })
      .addCase(fetchPolls.fulfilled, (state, action) => {
        state.loadingPolls = false;
        let polls = state.polls.data || [];
        if (action.meta.arg.xMsContinuationToken) {
          polls.push(...action.payload?.data?.surveys);
        } else {
          polls = [...action.payload?.data?.surveys];
        }
        state.polls = {
          data: polls,
          xMsContinuationToken: action.payload?.xMsContinuationToken,
        };
        state.totalPollCount = action.payload?.data?.totalSurveyCount;
      })
      .addCase(fetchPolls.rejected, (state, action) => {
        state.pollsError = action.payload;
        state.loadingPolls = false;
      })
      .addCase(fetchFilteredPolls.pending, (state) => {
        state.loadingFilteredPolls = true;
      })
      .addCase(fetchFilteredPolls.fulfilled, (state, action) => {
        state.loadingFilteredPolls = false;
        let filteredPolls = state.filteredPolls.data || [];
        if (action.meta.arg.xMsContinuationToken) {
          filteredPolls.push(...action.payload?.data?.surveys);
        } else {
          filteredPolls = [...action.payload?.data?.surveys];
        }
        state.filteredPolls = {
          data: filteredPolls,
          xMsContinuationToken: action.payload?.xMsContinuationToken,
        };
        state.totalFilteredPollCount = action.payload?.data?.totalSurveyCount;
      })
      .addCase(fetchFilteredPolls.rejected, (state) => {
        state.loadingFilteredPolls = false;
      })
      .addCase(fetchPoll.pending, (state) => {
        state.pollWithQuestionsList = undefined;
        state.pollWithQuestionsListLoading = true;
      })
      .addCase(fetchPoll.fulfilled, (state, action) => {
        state.pollWithQuestionsListLoading = false;
        state.pollWithQuestionsList = action.payload;
      })
      .addCase(fetchPoll.rejected, (state) => {
        state.pollWithQuestionsListLoading = false;
      })
      .addCase(submitPoll.pending, (state) => {
        state.loadingSubmitPoll = true;
      })
      .addCase(submitPoll.fulfilled, (state, action) => {
        state.loadingSubmitPoll = false;
        state.pollWithQuestionsList = action.payload;
        state.pollSubmissionError = undefined;
      })
      .addCase(submitPoll.rejected, (state, action) => {
        state.loadingSubmitPoll = false;
        state.pollSubmissionError = action.payload;
      })
      .addCase(getNumberOfActiveAndClosedPolls.fulfilled, (state, action) => {
        state.numberOfActivePolls = action.payload.open;
        state.numberOfClosedPolls = action.payload.closed;
      })
      .addCase(getNumberOfActiveAndClosedPolls.rejected, (state, action) => {
        state.numberOfActivePolls = 0;
        state.numberOfClosedPolls = 0;
      });
  },
});

export const {
  clearPolls,
  clearFilteredPolls,
  clearPollWithQuestions,
  setCreateNewPollDialogOpened,
  setEditedPoll,
} = pollsSlice.actions;

export default pollsSlice.reducer;
